/**
 * UnifiedInvoiceModal — Shared rich invoice detail modal with device cards,
 * payment history, warranty badges, and action buttons.
 * Used by both Sales.jsx (Invoices page) and RepairTicketDetail.jsx (Ticket page).
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import OnlineInvoiceDetailModal, {
  OI_STATUS_COLORS,
  OI_STATUS_LABELS,
  copyInvoiceLink,
  fmtInvoiceDate,
} from "./OnlineInvoiceDetailModal";
import TyroPaymentDialog from "./TyroPaymentDialog";
import RefundModal from "./RefundModal";
import PinPromptModal from "./PinPromptModal";
import InvoicePrintMenu from "./InvoicePrintMenu";
import TyroPaymentDetails from "./TyroPaymentDetails";
import EditManualPaymentModal, { canEditManualPayment } from "./EditManualPaymentModal";
import CorrectManualPaymentModal from "./CorrectManualPaymentModal";
import { DeviceIdentity } from "./DeviceIdentity";
import { StripeLinkPanel } from "./StripeLinkPanel";
import { HistoryPanel } from "./HistoryPanel";
import { ClockCounterClockwise } from "@phosphor-icons/react";
import {
  Receipt,
  CurrencyDollar,
  CreditCard,
  Globe,
  CopySimple,
  CheckCircle,
  Clock,
  X,
  Trash,
  Prohibit,
  ArrowCounterClockwise,
  ChatCircleText,
  EnvelopeSimple,
  PencilSimple,
  PaperPlaneTilt,
  Download,
  ArrowSquareOut,
  Star,
  UserCircle,
  Phone,
  Warning,
  Eye,
  CaretDown,
  DotsThree,
  CalendarBlank,
} from "@phosphor-icons/react";
import { useStore } from "../context/StoreContext";
import { CustomerAvatar } from "./CustomerAvatar";

const FOOT_BTN = "h-10 px-3.5 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,border-color,transform]";
const MENU_TONES = { default: "text-slate-800", amber: "text-amber-700", red: "text-rose-600" };
function MenuItem({ icon: Icon, onClick, children, testid, tone = "default" }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-semibold hover:bg-slate-50 transition-colors ${MENU_TONES[tone]}`}>
      <Icon size={15} weight="bold" className={tone === "default" ? "text-slate-600" : ""} /> {children}
    </button>
  );
}
function SummaryTile({ label, value, tone = "text-slate-900", testid }) {
  return (
    <div className="px-2.5 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-white border border-slate-200 min-w-0 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-14px_rgba(16,24,40,0.25)] transition-[transform,box-shadow] duration-200" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</div>
      <div className={`text-[14px] sm:text-[19px] font-bold font-mono tabular-nums mt-0.5 truncate ${tone}`}>{value}</div>
    </div>
  );
}

function WarrantyPill({ w }) {
  if (!w) return null;
  if (w.active) {
    const soon = w.expiring_soon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border shrink-0 ${soon ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-200"}`} data-testid="item-warranty-pill">
        {soon ? <Clock size={10} weight="fill" className="text-amber-600" /> : <CheckCircle size={10} weight="fill" className="text-emerald-600" />}
        <span className={`text-[9px] font-bold uppercase tracking-wider ${soon ? "text-amber-700" : "text-emerald-700"}`}>{w.label}</span>
      </span>
    );
  }
  if (w.label === "Expired") {
    return <span className="inline-flex px-2 py-0.5 bg-red-50 border border-red-200 rounded-full text-[9px] font-bold uppercase tracking-wider text-red-600 shrink-0" data-testid="item-warranty-pill">Warranty expired</span>;
  }
  return null;
}


const METHOD_LABELS = {
  cash: "Cash",
  tyro: "Tyro EFTPOS",
  card: "Card",
  card_manual: "Card (Manual)",
  stripe: "Stripe",
  other: "Other",
  store_credit: "Store Credit",
};

const METHOD_ICONS = {
  cash: CurrencyDollar,
  card: CreditCard,
  card_manual: CreditCard,
  tyro: CreditCard,
  stripe: Globe,
  other: Receipt,
};

const TYRO_LOGO = () => (
  <img src="/tyro-logo.gif" alt="Tyro" className="h-6 w-auto" />
);

export default function UnifiedInvoiceModal({ invoice, onClose, onUpdated, onAddPayment, user }) {
  const { currentStore } = useStore();
  const isVoided = invoice.status === "voided";
  const inStorePaid = parseFloat(invoice.in_store_paid || 0);
  // A Tyro tender is stored as card_manual + tyro_meta; tyro_meta is the real signal.
  const payments = (invoice.payment_history || []).map((p) => (p.tyro_meta && p.method !== "tyro" ? { ...p, method: "tyro" } : p));
  const fromHistory = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : p.amount), 0);
  const totalPaid = fromHistory > 0 ? fromHistory : (inStorePaid + parseFloat(invoice.stripe_paid_total || 0));
  const totalRefunded = Math.max(
    parseFloat(invoice.refund_total || 0),
    payments.reduce((s, p) => s + (p.is_refund ? Math.abs(p.amount || 0) : 0), 0),
  );
  const refundReset = parseFloat(invoice.refund_reset_amount || 0);
  const grossEff = Math.max(0, totalPaid - refundReset);
  const refundEff = Math.max(0, totalRefunded - refundReset);
  const refundClosed = grossEff > 0.005 && refundEff >= grossEff - 0.005;
  const reopenedAfterRefund = refundReset > 0.005 && !refundClosed;
  const refundState = refundEff > 0.005
    ? (refundClosed ? "refunded" : "part_refunded")
    : null;
  const netPaid = Math.round((totalPaid - totalRefunded) * 100) / 100;
  const invoiceTotal = parseFloat(invoice.total || 0);
  const overpaidAmt = Math.round(Math.max(0, netPaid - invoiceTotal) * 100) / 100;
  const storedDue = invoice.amount_due != null ? parseFloat(invoice.amount_due) : invoiceTotal;
  // Full refund closes the invoice. A part refund is a discount (gross paid counts).
  // After a reopen, refunds taken before it no longer cancel anything.
  const amountDue = payments.length
    ? (refundClosed ? 0 : Math.round(Math.max(0, invoiceTotal - grossEff) * 100) / 100)
    : storedDue;
  const isPaid = !isVoided && amountDue <= 0.005;
  const hasPayments = totalPaid > 0.005;

  const [editMode, setEditMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [addPayOpen, setAddPayOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [sendPopup, setSendPopup] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [payMenu, setPayMenu] = useState(null); // null | "menu" | "stripe"
  const [stripeAmt, setStripeAmt] = useState("");
  const [stripeSending, setStripeSending] = useState(false);
  const [reviewPopup, setReviewPopup] = useState({ open: false, phone: "" });
  const [reviewSending, setReviewSending] = useState(false);
  const [correctingPayment, setCorrectingPayment] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const refreshLinks = async (id = linkData?.id || (invoice._is_sale ? null : invoice.id)) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/online-invoices/${id}/detail`);
      setLinkData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reviewUrl = (invoice.store?.google_review_url || currentStore?.google_review_url || "").trim();
  const storeName = invoice.store?.name || currentStore?.name || "us";

  // Permission-gated PIN prompt — void/delete always require the caller's
  // PIN (own staff PIN or admin PIN), matching the backend's
  // require_pin_and_permission gate. `key` picks the permission the
  // backend checks; `adminOnly` mirrors DELETE /sales/{id} (admin PIN only).
  const [pinAction, setPinAction] = useState(null); // { key, label, fn, adminOnly }

  const requirePermission = (permKey, label, fn, adminOnly = false) => {
    setPinAction({ key: permKey, label, fn, adminOnly });
  };

  if (editMode) {
    return (
      <OnlineInvoiceDetailModal
        invoice={invoice}
        onClose={() => setEditMode(false)}
        onUpdated={onUpdated}
      />
    );
  }

  const items = invoice.items || [];
  const devices = invoice.devices || [];
  // Build a set of descriptions already shown inside device repair cards
  const deviceRepairNames = new Set();
  for (const dev of devices) {
    for (const r of (dev.repairs || [])) {
      deviceRepairNames.add(r.name);
    }
  }
  // Accessories = any invoice line item NOT already rendered inside a device card
  // and not a repair line itself (repair items have kind="repair" or repair_ticket_id)
  const accessories = items.filter((it) =>
    !deviceRepairNames.has(it.description) && it.kind !== "repair" && !it.repair_ticket_id
  );

  const handleSend = async (via) => {
    setSending(true);
    try {
      // "Send" = a copy of the invoice/receipt only (no payment link). Uses the
      // backing POS sale when there is one; payment links live under Take payment.
      const copySaleId = invoice._is_sale ? invoice.id : (invoice.backing_sale_id || invoice.sale_id || null);
      if (copySaleId) {
        // Plain sale (no linked online invoice) — use sale-specific endpoints
        const errs = [];
        if (via === "sms" || via === "both") {
          const phone = invoice.customer_phone;
          if (!phone) { errs.push("No phone number on this customer"); }
          else {
            try { await api.post(`/sales/${copySaleId}/sms`, { to: phone }); }
            catch (e) { errs.push(`SMS: ${formatApiError(e)}`); }
          }
        }
        if (via === "email" || via === "both") {
          const email = invoice.customer_email;
          if (!email) { errs.push("No email address on this customer"); }
          else {
            try { await api.post(`/sales/${copySaleId}/email`, { to: email }); }
            catch (e) { errs.push(`Email: ${formatApiError(e)}`); }
          }
        }
        if (errs.length) { errs.forEach((e) => toast.error(e)); return; }
        else toast.success(via === "both" ? "Sent via SMS & Email" : via === "sms" ? "Sent via SMS" : "Sent via Email");
      } else {
        // Online invoice — use the unified send endpoint
        const { data } = await api.post(`/online-invoices/${invoice.id}/send`, { via });
        if (data.notification?.errors?.length) {
          data.notification.errors.forEach((e) => toast.error(e));
        } else {
          toast.success(via === "both" ? "Sent via SMS & Email" : via === "sms" ? "Sent via SMS" : "Sent via Email");
        }
        if (!["sms_status", "email_status"].some((k) => data.notification?.[k] === "sent")) return;
      }
      onUpdated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const sendStripeLink = async () => {
    const amt = Math.round(Number(stripeAmt) * 100) / 100;
    if (!(amt > 0)) return toast.error("Enter an amount");
    if (amt > amountDue + 0.01) return toast.error(`Amount can't be more than the $${money(amountDue)} owing`);
    if (!invoice.customer_phone && !invoice.customer_email) return toast.error("Add a phone or email on this customer first");
    setStripeSending(true);
    try {
      const url = invoice._is_sale ? `/sales/${invoice.id}/stripe-link` : `/online-invoices/${invoice.id}/stripe-link`;
      const { data } = await api.post(url, { amount: amt, send_via: "both" });
      const delivered = ["sms_status", "email_status"].some((k) => data.notification?.[k] === "sent");
      if (delivered) toast.success(`Stripe payment link for $${money(amt)} sent to ${invoice.customer_name || "customer"}`);
      else toast.error("Link created, but it couldn't be delivered — check the customer's phone and email");
      (data.notification?.errors || []).forEach((m) => toast.error(m));
      setPayMenu(null);
      refreshLinks(data.invoice_id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStripeSending(false); }
  };

  const copyLink = async () => {
    if (invoice.payment_token) return copyInvoiceLink(invoice);
    const sid = invoice._is_sale ? invoice.id : (invoice.backing_sale_id || invoice.sale_id);
    if (!sid) return toast.error("No shareable link for this invoice yet");
    try {
      const { data } = await api.get(`/sales/${sid}/receipt-url`);
      await navigator.clipboard.writeText(data.html || data.pdf);
      toast.success("Invoice link copied!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleSendReview = async () => {
    const phone = (reviewPopup.phone || "").trim();
    if (!phone) return toast.error("Enter a phone number");
    if (reviewSending) return;
    setReviewSending(true);
    try {
      await api.post(`/sales/${invoice.id}/review-sms`, { to: phone });
      toast.success(`Review link sent to ${phone}`);
      setReviewPopup({ open: false, phone: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReviewSending(false); }
  };

  const handleVoid = async (pin) => {
    setActionLoading("void");
    try {
      const header = user?.role === "admin" ? "X-Admin-Pin" : "X-Staff-Pin";
      const actionId = invoice.backing_sale_id || (invoice._is_sale ? invoice.id : null);
      const url = actionId ? `/sales/${actionId}/void` : `/online-invoices/${invoice.id}/void`;
      await api.post(url, {}, { headers: { [header]: pin } });
      toast.success(`${invoice.invoice_number} voided`);
      onUpdated();
    } finally { setActionLoading(""); }
  };

  const handleDelete = async (pin) => {
    setActionLoading("delete");
    try {
      const actionId = invoice.backing_sale_id || (invoice._is_sale ? invoice.id : null);
      const url = actionId ? `/sales/${actionId}` : `/online-invoices/${invoice.id}`;
      await api.delete(url, { headers: { "X-Admin-Pin": pin } });
      toast.success(`${invoice.invoice_number} deleted`);
      onUpdated();
    } finally { setActionLoading(""); }
  };

  const handlePdf = () => {
    api.get(`/online-invoices/${invoice.id}/pdf`, { responseType: "blob" })
      .then(({ data }) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(data);
        a.download = `${invoice.invoice_number}.pdf`;
        a.click();
      })
      .catch(() => toast.error("PDF download failed"));
  };

  const handleAddPayment = () => {
    if (onAddPayment) {
      onAddPayment(invoice);
    } else {
      setAddPayOpen(true);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-5 animate-in fade-in duration-150" onClick={onClose}>
        <div
          className="bg-white w-full max-w-[58rem] rounded-2xl sm:rounded-[28px] shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] border border-slate-200 overflow-hidden max-h-[95vh] flex flex-col animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
          data-testid="unified-invoice-modal"
        >
          {/* Top accent */}
          <div className={`h-1.5 ${isPaid ? "bg-emerald-500" : isVoided ? "bg-zinc-300" : amountDue > 0 ? "bg-amber-400" : "bg-zinc-200"}`} />

          {/* Header */}
          <div className="px-5 sm:px-8 pt-5 pb-5 bg-white text-slate-900 border-b border-slate-200">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-slate-500">Tax invoice</div>
                <h2 className="font-heading text-[22px] sm:text-[34px] leading-tight whitespace-nowrap font-bold text-slate-900 tracking-tight font-mono">{invoice.invoice_number}</h2>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-slate-500">
                  {invoice.created_at && (
                    <span className="inline-flex items-center gap-1" data-testid="inv-created-at">
                      <CalendarBlank size={13} weight="bold" />
                      {new Date(invoice.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                  {invoice.linked_ticket_id && (
                    <Link
                      to={`/repairs/${invoice.linked_ticket_id}`}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-[#1E5FEB] hover:border-[#BFDBFE] hover:bg-[#F5F9FF] active:scale-[0.98] transition-[background-color,border-color,transform]"
                      data-testid="inv-view-ticket"
                      onClick={onClose}
                    >
                      <ArrowSquareOut size={13} weight="bold" /> View ticket
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2.5 shrink-0">
                {overpaidAmt > 0.005 ? (
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider bg-red-50 border border-red-200 text-red-700" data-testid="inv-status-badge">
                    <Warning size={14} weight="fill" /> Overpaid
                  </span>
                ) : refundState ? (
                  <span className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider border ${refundState === "refunded" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-orange-50 border-orange-200 text-orange-700"}`} data-testid="inv-status-badge">
                    <ArrowCounterClockwise size={14} weight="bold" /> {refundState === "refunded" ? "Refunded" : "Part refunded"}
                  </span>
                ) : isPaid ? (
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-50 border border-emerald-200 text-emerald-700" data-testid="inv-status-badge">
                    <CheckCircle size={14} weight="fill" /> Paid
                  </span>
                ) : isVoided ? (
                  <span className="inline-flex items-center h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider bg-zinc-100 border border-zinc-200 text-zinc-500" data-testid="inv-status-badge">
                    Voided
                  </span>
                ) : inStorePaid > 0.005 ? (
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 text-amber-700" data-testid="inv-status-badge">
                    <Clock size={14} weight="fill" /> Partial
                  </span>
                ) : (
                  <span className={`inline-flex items-center h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider border ${OI_STATUS_COLORS[invoice.status] || "bg-zinc-100 text-zinc-600"}`} data-testid="inv-status-badge">
                    {OI_STATUS_LABELS[invoice.status] || invoice.status}
                  </span>
                )}
                {invoice.viewed_at && invoice.sent_at && (
                  <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider bg-blue-50 border border-blue-200 text-blue-700" data-testid="inv-viewed-badge">
                    <Eye size={14} weight="fill" /> Viewed{invoice.view_count > 1 ? ` ${invoice.view_count}x` : ""}
                  </span>
                )}
                <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors" data-testid="inv-close">
                  <X size={18} weight="bold" />
                </button>
              </div>
            </div>

            {/* Money summary */}
            <div className="mt-4 grid grid-cols-3 gap-2.5" data-testid="inv-summary">
              <SummaryTile label="Total" value={`$${money(invoice.total)}`} tone="text-slate-900" testid="inv-summary-total" />
              <SummaryTile label={totalRefunded > 0.005 ? `Paid · $${money(totalRefunded)} refunded` : "Paid"}
                           value={`$${money(Math.max(0, netPaid))}`} tone="text-emerald-600" testid="inv-summary-paid" />
              <SummaryTile
                label={isVoided ? "Voided" : amountDue > 0.005 ? "Balance due" : "Balance"}
                value={`$${money(isVoided ? 0 : amountDue)}`}
                tone={amountDue > 0.005 && !isVoided ? "text-amber-600" : "text-slate-400"}
                testid="inv-summary-balance"
              />
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 space-y-6 bg-[#FCFDFE]">

            {/* ── Customer Card ── */}
            {(invoice.customer_name && invoice.customer_name !== "Walk-in") ? (
              <div className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(30,95,235,0.25)] hover:border-[#BFDBFE] transition-[box-shadow,border-color]" data-testid="inv-customer-card">
                {invoice.customer_id ? (
                  <Link
                    to={`/customers/${invoice.customer_id}`}
                    onClick={onClose}
                    className="group flex items-start justify-between gap-3 p-4 hover:bg-[#F5F9FF] transition-colors"
                    data-testid="inv-customer-link"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <CustomerAvatar seed={invoice.customer_id} size={44} className="group-hover:scale-105 transition-transform" testId="inv-customer-avatar" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">Customer</div>
                        <div className="text-[16px] font-bold text-[#121216] leading-tight mt-0.5 truncate">{invoice.customer_name}</div>
                        {invoice.customer_phone && (
                          <div className="flex items-center gap-1.5 mt-1 text-[13px] font-mono font-semibold text-[#121216]">
                            <Phone size={12} weight="bold" className="text-[#4B5563]" />
                            {invoice.customer_phone}
                          </div>
                        )}
                        {invoice.customer_email && (
                          <div className="text-[11px] text-[#6B7280] truncate mt-0.5">{invoice.customer_email}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0 mt-1">
                      <ArrowSquareOut size={14} weight="bold" className="text-[#9CA3AF] group-hover:text-[#1E5FEB] transition-colors" />
                      <span className="text-[10px] font-semibold text-[#1E5FEB] tracking-wider uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                        Full history
                      </span>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-start gap-3 p-4">
                    <CustomerAvatar seed={invoice.customer_name} size={44} testId="inv-customer-avatar" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">Customer</div>
                      <div className="text-[16px] font-bold text-[#121216] leading-tight mt-0.5 truncate">{invoice.customer_name}</div>
                      {invoice.customer_phone && (
                        <div className="flex items-center gap-1.5 mt-1 text-[13px] font-mono font-semibold text-[#121216]">
                          <Phone size={12} weight="bold" className="text-[#4B5563]" />
                          {invoice.customer_phone}
                        </div>
                      )}
                      {invoice.customer_email && (
                        <div className="text-[11px] text-[#6B7280] truncate mt-0.5">{invoice.customer_email}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-[#E5E7EB] p-4 flex items-center gap-3" data-testid="inv-customer-card">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <UserCircle size={20} weight="bold" className="text-[#9CA3AF]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF] font-semibold">Customer</div>
                  <div className="text-[15px] font-semibold text-[#121216] mt-0.5">Walk-in</div>
                </div>
              </div>
            )}

            {/* ── Customer Viewed Indicator ── */}
            {invoice.viewed_at && invoice.sent_at && (
              <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-3" data-testid="inv-customer-viewed-info">
                <Eye size={16} weight="fill" className="text-blue-600 shrink-0" />
                <div className="min-w-0">
                  <span className="text-[12px] font-semibold text-blue-800">Customer opened this invoice</span>
                  {invoice.viewed_at && (
                    <span className="text-[11px] text-blue-600 ml-1.5">
                      — {fmtInvoiceDate(invoice.viewed_at)}
                    </span>
                  )}
                  {invoice.view_count > 1 && (
                    <span className="text-[11px] text-blue-500 ml-1.5">
                      ({invoice.view_count} views)
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── Stripe payment link: status, reminder, cancel, timeline ── */}
            {reopenedAfterRefund && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center gap-2.5" data-testid="inv-reopened-banner">
                <ArrowCounterClockwise size={16} weight="bold" className="text-amber-600 shrink-0" />
                <span className="text-[13px] font-semibold text-amber-800">
                  Reopened after refund — new charges ${money(invoiceTotal)}
                </span>
                <span className="text-[11px] text-amber-700 ml-auto">Earlier ${money(refundReset)} payment was refunded</span>
              </div>
            )}
            <StripeLinkPanel invoice={linkData || invoice} onUpdated={refreshLinks} />

            {/* ── Devices & Charges ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#9CA3AF]">Charges</div>
                {!isVoided && (
                  <button onClick={() => requirePermission("sale.edit", "Edit invoice items", () => setEditMode(true))} className="h-8 px-3 rounded-xl border border-slate-200 bg-white text-[12px] font-semibold text-[#1E5FEB] hover:border-[#BFDBFE] hover:bg-[#F5F9FF] active:scale-[0.98] flex items-center gap-1.5 transition-[background-color,border-color,transform]" data-testid="edit-items-btn">
                    <PencilSimple size={12} weight="bold" /> Edit Items
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {devices.length > 0 ? devices.map((dev, di) => (
                  <div key={di} className="rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid={`device-card-${di}`}>
                    <div className="px-4 py-3 bg-[#FAFBFC] border-b border-[#E5E7EB] flex items-center justify-between gap-3">
                      <DeviceIdentity index={di + 1} make={dev.device_make} type={dev.device_type} model={dev.device_model} year={dev.device_year} modelNumber={dev.device_model_number} compact />
                      <div className="flex items-center gap-2 shrink-0">
                        {dev.warranty?.active && dev.warranty?.expiring_soon ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-300 rounded-full animate-pulse">
                            <Clock size={10} weight="fill" className="text-amber-600" />
                            <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">{dev.warranty.label}</span>
                          </span>
                        ) : dev.warranty?.active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                            <CheckCircle size={10} weight="fill" className="text-emerald-600" />
                            <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">{dev.warranty.label}</span>
                          </span>
                        ) : dev.warranty?.label === "Expired" ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-red-50 border border-red-200 rounded-full">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">Expired</span>
                          </span>
                        ) : dev.warranty?.label?.includes("pending") ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-full">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{dev.warranty.months}mo warranty</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-full">
                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{dev.warranty?.months || 12}mo warranty</span>
                          </span>
                        )}
                        <span className="font-mono text-[13px] font-bold text-[#121216] tabular-nums">
                          ${money(dev.estimated_cost || 0)}
                        </span>
                      </div>
                    </div>
                    {(dev.repairs || []).map((r, ri) => (
                      <div key={ri} className="flex items-center justify-between px-5 py-2.5 border-b border-[#F3F4F6] last:border-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-[#1E5FEB] ring-2 ring-[#DBEAFE] shrink-0" />
                          <span className="text-[13px] text-[#121216]">{r.name}</span>
                        </div>
                        <span className="font-mono text-[13px] text-[#9CA3AF] tabular-nums shrink-0 ml-4">
                          ${money(r.price || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )) : (
                  <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
                    {items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-[#F3F4F6] last:border-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-[#1E5FEB] ring-2 ring-[#DBEAFE] shrink-0" />
                          <span className="text-[14px] text-[#121216]">{it.description}</span>
                          <WarrantyPill w={(invoice.item_warranties || []).find((iw) => iw.name === it.description)?.warranty} />
                          {it.quantity > 1 && <span className="text-[12px] text-[#9CA3AF]">x{it.quantity}</span>}
                        </div>
                        <span className="font-mono text-[14px] font-semibold text-[#121216] tabular-nums shrink-0 ml-4">
                          ${money((it.unit_price || 0) * (it.quantity || 1))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {accessories.length > 0 && devices.length > 0 && (
                  <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
                    <div className="px-4 py-2.5 bg-amber-50/50 border-b border-[#E5E7EB] text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                      Accessories
                    </div>
                    {accessories.map((it, i) => {
                      // Match accessory warranty from backend enrichment
                      const accWarranties = invoice.accessories_warranty || [];
                      const matchW = accWarranties.find((aw) => aw.name === it.description);
                      const w = matchW?.warranty || (invoice.item_warranties || []).find((iw) => iw.name === it.description)?.warranty;
                      return (
                        <div key={i} className="flex items-center justify-between px-5 py-2.5 border-b border-[#F3F4F6] last:border-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                            <span className="text-[13px] text-[#121216]">{it.description}</span>
                            {it.quantity > 1 && <span className="text-[12px] text-[#9CA3AF]">x{it.quantity}</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-4">
                            {w?.active && w?.expiring_soon ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-300 rounded-full animate-pulse">
                                <Clock size={10} weight="fill" className="text-amber-600" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">{w.label}</span>
                              </span>
                            ) : w?.active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                                <CheckCircle size={10} weight="fill" className="text-emerald-600" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">{w.label}</span>
                              </span>
                            ) : w?.label === "Expired" ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-red-50 border border-red-200 rounded-full">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">Expired</span>
                              </span>
                            ) : null}
                            <span className="font-mono text-[13px] font-semibold text-[#121216] tabular-nums">
                              ${money((it.unit_price || 0) * (it.quantity || 1))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Trade-in items with warranty */}
                {(invoice.accessories_warranty || []).filter((aw) => aw.name?.toLowerCase().includes("trade")).length > 0 && (
                  <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-[#E5E7EB] text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                      Trade-in Devices
                    </div>
                    {(invoice.accessories_warranty || []).filter((aw) => aw.name?.toLowerCase().includes("trade")).map((aw, i) => {
                      const w = aw.warranty;
                      return (
                        <div key={i} className="flex items-center justify-between px-5 py-2.5 border-b border-[#F3F4F6] last:border-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="w-2 h-2 rounded-full bg-[#1E5FEB] ring-2 ring-[#DBEAFE] shrink-0" />
                            <span className="text-[13px] text-[#121216]">{aw.name}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-4">
                            {w?.active && w?.expiring_soon ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-300 rounded-full animate-pulse">
                                <Clock size={10} weight="fill" className="text-amber-600" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700">{w.label}</span>
                              </span>
                            ) : w?.active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                                <CheckCircle size={10} weight="fill" className="text-emerald-600" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">{w.label}</span>
                              </span>
                            ) : w?.label === "Expired" ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-red-50 border border-red-200 rounded-full">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">Expired</span>
                              </span>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {parseFloat(invoice.discount || 0) > 0.005 && (
                  <div className="flex items-center justify-between px-5 py-2.5 rounded-2xl border border-[#E5E7EB]" data-testid="inv-discount">
                    <span className="text-[12px] uppercase tracking-[0.14em] font-bold text-[#4B5563]">Discount</span>
                    <span className="font-mono text-[14px] font-semibold text-red-600 tabular-nums">-${money(invoice.discount)}</span>
                  </div>
                )}
                {parseFloat(invoice.store_credit_redeemed || 0) > 0.005 && (
                  <div className="flex items-center justify-between px-5 py-2.5 rounded-2xl border border-emerald-200 bg-emerald-50" data-testid="inv-store-credit">
                    <span className="text-[12px] uppercase tracking-[0.14em] font-bold text-emerald-800">Store credit used</span>
                    <span className="font-mono text-[14px] font-semibold text-emerald-700 tabular-nums">-${money(invoice.store_credit_redeemed)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between px-5 py-3.5 bg-[#FAFBFC] rounded-2xl border border-[#E5E7EB]">
                  <span className="text-[12px] uppercase tracking-[0.14em] font-bold text-[#4B5563]">Total Charges</span>
                  <span className="font-mono text-[17px] font-bold text-[#121216] tabular-nums">${money(invoice.total)}</span>
                </div>
              </div>
            </div>

            {/* ── Payment History ── */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#9CA3AF] mb-3">Payment History</div>
              {payments.length === 0 && !isPaid ? (
                <div className="text-[14px] text-[#9CA3AF] italic py-4 text-center">No payments recorded yet</div>
              ) : (
                <div className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
                  {payments.map((p, i) => {
                    const isTyro = p.method === "tyro";
                    const isRefund = p.is_refund || p.amount < 0;
                    const isCorrected = !!p.corrected_at;
                    const canCorrect = user?.role === "admin"
                      && !!(invoice._is_sale ? invoice.id : p.sale_id)
                      && !isCorrected
                      && !isRefund
                      && !p.tyro_meta
                      && p.source !== "stripe"
                      && ["cash", "card", "card_manual", "other"].includes((p.method || "").toLowerCase());
                    const canEdit = user?.role === "admin"
                      && !!(invoice._is_sale ? invoice.id : p.sale_id)
                      && canEditManualPayment(p);
                    const Icon = isTyro ? null : (METHOD_ICONS[p.method] || Receipt);
                    return (
                      <div key={p.payment_id || i} className={`flex items-center justify-between px-5 py-3 border-b border-[#F3F4F6] last:border-0 ${isRefund ? "bg-red-50/40" : isCorrected ? "bg-slate-50 opacity-70" : ""}`} data-testid={`payment-history-entry-${p.payment_id || i}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {isTyro ? (
                            <div className="shrink-0"><TYRO_LOGO /></div>
                          ) : (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isRefund ? "bg-red-50" : "bg-emerald-50"}`}>
                              {isRefund ? (
                                <ArrowCounterClockwise size={16} weight="bold" className="text-red-600" />
                              ) : (
                                <Icon size={16} weight="bold" className="text-emerald-600" />
                              )}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className={`text-[14px] font-semibold ${isRefund ? "text-red-700" : isCorrected ? "text-slate-500" : "text-[#121216]"}`}>
                              {isRefund ? "Refund" : isCorrected ? "Corrected" : ""} {METHOD_LABELS[p.method] || p.method}
                              {p.tyro_offline && <span className="ml-1.5 text-[11px] font-medium text-slate-500" data-testid={`payment-tyro-terminal-${p.payment_id || i}`}>(terminal{p.tyro_auth_code ? ` · auth ${p.tyro_auth_code}` : ""})</span>}
                            </div>
                            <div className="text-[12px] text-[#9CA3AF] truncate" title={p.edited_at ? `Edited by ${p.edited_by_name || "admin"}: ${p.edit_reason}` : undefined}>
                              {fmtInvoiceDate(p.paid_at)} · {p.cashier || "Staff"}
                              {p.note && p.note.includes("—") ? ` · ${p.note.split("—")[1]?.trim()}` : ""}
                              {isCorrected && p.correction_reason ? ` · ${p.correction_reason}` : ""}
                              {p.edited_at ? ` · Edited: ${p.edit_reason}` : ""}
                            </div>
                            {p.tyro_meta && (
                              <TyroPaymentDetails
                                meta={p.tyro_meta}
                                saleId={p.sale_id || (invoice._is_sale ? invoice.id : invoice.backing_sale_id) || null}
                                paymentIndex={p.payment_index}
                                testId={`payment-tyro-details-${p.payment_id || i}`}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isRefund ? (
                            <span className="font-mono text-[15px] font-bold text-red-600 tabular-nums">
                              -${money(Math.abs(p.amount))}
                            </span>
                          ) : isCorrected ? (
                            <span className="font-mono text-[13px] font-semibold text-slate-500 tabular-nums line-through">${money(p.amount)}</span>
                          ) : (
                            <>
                              <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                              <span className="font-mono text-[15px] font-bold text-emerald-700 tabular-nums">
                                ${money(p.amount)}
                              </span>
                            </>
                          )}
                          {canEdit && (
                            <button onClick={() => setEditingPayment(p)} className="h-8 px-2.5 rounded-lg border border-slate-300 text-[10px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors" data-testid={`edit-manual-payment-${p.payment_id || i}`}>
                              Edit
                            </button>
                          )}
                          {canCorrect && (
                            <button onClick={() => setCorrectingPayment(p)} className="h-8 px-2.5 rounded-lg border border-amber-300 text-[10px] font-semibold text-amber-800 hover:bg-amber-50 transition-colors" data-testid={`correct-manual-payment-${p.payment_id || i}`}>
                              Correct
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {totalPaid > 0.005 && (
                    <div className="flex items-center justify-between px-5 py-3.5 bg-emerald-50/60 border-t border-[#E5E7EB]">
                      <span className="text-[12px] uppercase tracking-[0.14em] font-bold text-emerald-700">
                        Total Paid{totalRefunded > 0.005 ? " (after refunds)" : ""}
                      </span>
                      <span className="font-mono text-[17px] font-bold text-emerald-700 tabular-nums" data-testid="inv-total-paid">${money(Math.max(0, netPaid))}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Balance ── */}
            {overpaidAmt > 0.005 ? (
              <div className="rounded-2xl px-5 py-5 bg-red-50 border-2 border-red-300" data-testid="inv-balance">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[12px] uppercase tracking-[0.14em] font-bold text-red-700">
                      Overpaid — Refund Due
                    </div>
                    <div className="text-[12px] text-red-600 mt-0.5">
                      Customer paid ${money(netPaid)} on a ${money(invoiceTotal)} repair
                    </div>
                  </div>
                  <span className="font-mono text-3xl font-bold tabular-nums text-red-700">
                    ${money(overpaidAmt)}
                  </span>
                </div>
              </div>
            ) : refundState ? (
              <div className="rounded-2xl px-5 py-5 flex items-center justify-between bg-rose-50 border-2 border-rose-300" data-testid="inv-balance">
                <div>
                  <div className="text-[12px] uppercase tracking-[0.14em] font-bold text-rose-700">
                    {refundState === "refunded" ? "Fully Refunded" : "Part Refunded"}
                  </div>
                  <div className="text-[12px] text-rose-600 mt-0.5" data-testid="inv-refund-net">
                    Paid ${money(totalPaid)} · kept ${money(Math.max(0, netPaid))}{amountDue > 0.005 ? ` · $${money(amountDue)} still owing` : ""}
                  </div>
                </div>
                <span className="font-mono text-3xl font-bold tabular-nums text-rose-700">-${money(totalRefunded)}</span>
              </div>
            ) : (
              <div className={`rounded-2xl px-5 py-5 flex items-center justify-between ${
                isPaid
                  ? "bg-emerald-50 border-2 border-emerald-300"
                  : "bg-amber-50 border-2 border-amber-300"
              }`} data-testid="inv-balance">
                <div>
                  <div className={`text-[12px] uppercase tracking-[0.14em] font-bold ${isPaid ? "text-emerald-700" : "text-amber-800"}`}>
                    {isPaid ? "Fully Paid" : "Balance Remaining"}
                  </div>
                  {!isPaid && ((linkData || invoice).stripe_links || []).some((l) => l.status === "active" && l.sent_via) && (
                    <div className="text-[12px] text-amber-600 mt-0.5">Payment link sent to customer</div>
                  )}
                </div>
                <span className={`font-mono text-3xl font-bold tabular-nums ${isPaid ? "text-emerald-700" : "text-amber-800"}`}>
                  ${money(isPaid ? 0 : amountDue)}
                </span>
              </div>
            )}
          </div>

          {/* ── Action Bar ── */}
          <div className="border-t border-slate-200 bg-white shadow-[0_-8px_24px_-16px_rgba(15,23,42,0.25)]">

            <div className="px-5 sm:px-8 py-3.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                {!isVoided && !isPaid && (
                  <div className="relative">
                    <button
                      onClick={() => setPayMenu((v) => (v ? null : "menu"))}
                      className="h-10 px-4 rounded-xl bg-emerald-600 text-white text-[12.5px] font-bold hover:bg-emerald-700 shadow-[0_6px_16px_-8px_rgba(5,150,105,0.8)] flex items-center gap-1.5 active:scale-[0.98] transition-[background-color,transform]"
                      data-testid="inv-add-payment"
                    >
                      <CurrencyDollar size={16} weight="bold" /> Take payment · ${money(amountDue)}
                      <CaretDown size={11} weight="bold" className="opacity-80" />
                    </button>
                    {payMenu && (
                      <>
                        <div className="fixed inset-0 z-[10]" onClick={() => setPayMenu(null)} />
                        <div className="absolute bottom-full left-0 mb-2 z-[11] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden w-72 py-1" data-testid="inv-pay-menu">
                          {payMenu === "menu" ? (
                            <>
                              <MenuItem icon={CurrencyDollar} onClick={() => { setPayMenu(null); handleAddPayment(); }} testid="inv-pay-instore">
                                Record in-store payment
                              </MenuItem>
                              {(!invoice._is_sale || !!invoice.linked_ticket_id) ? (
                                <MenuItem icon={CreditCard} onClick={() => { setStripeAmt(String(amountDue.toFixed(2))); setPayMenu("stripe"); }} testid="inv-pay-stripe">
                                  Send Stripe payment link
                                </MenuItem>
                              ) : (
                                <div className="px-3.5 py-2.5 text-[11.5px] text-slate-400 flex items-center gap-2" data-testid="inv-pay-stripe-disabled">
                                  <CreditCard size={15} weight="bold" /> Stripe link — repair tickets only
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="p-3.5 space-y-2.5" data-testid="inv-stripe-form">
                              <div className="text-[12px] font-semibold text-slate-800">Stripe payment link</div>
                              <div className="text-[11px] text-slate-500">Texted & emailed to {invoice.customer_phone || invoice.customer_email || "the customer"}. Use a smaller amount for a deposit.</div>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
                                <input type="number" min="0" step="0.01" value={stripeAmt} onChange={(e) => setStripeAmt(e.target.value)}
                                       className="w-full h-10 pl-7 pr-3 rounded-lg border border-slate-200 text-[14px] tabular-nums focus:border-slate-900 outline-none" data-testid="inv-stripe-amount" />
                              </div>
                              <button onClick={sendStripeLink} disabled={stripeSending}
                                      className="w-full h-10 rounded-lg bg-slate-900 text-white text-[12.5px] font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-1.5" data-testid="inv-stripe-send">
                                <PaperPlaneTilt size={14} weight="bold" /> {stripeSending ? "Sending…" : "Send payment link"}
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {/* Send (split button) */}
                <div className="relative">
                  <button
                    onClick={() => setSendPopup((v) => !v)}
                    disabled={sending}
                    className="h-10 pl-3.5 pr-3 rounded-xl bg-[#1E5FEB] text-white text-[12.5px] font-semibold hover:bg-[#1849C8] shadow-[0_6px_16px_-8px_rgba(30,95,235,0.8)] hover:-translate-y-px disabled:opacity-50 flex items-center gap-2 active:scale-[0.98] transition-[background-color,transform]"
                    data-testid="inv-send-btn"
                  >
                    <PaperPlaneTilt size={15} weight="bold" /> {sending ? "Sending…" : "Send invoice"}
                    <CaretDown size={11} weight="bold" className="opacity-80" />
                  </button>
                  {sendPopup && (
                    <>
                      <div className="fixed inset-0 z-[10]" onClick={() => setSendPopup(false)} />
                      <div className="absolute bottom-full left-0 mb-2 z-[11] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden w-52 py-1" data-testid="send-popup">
                        <MenuItem icon={ChatCircleText} onClick={() => { setSendPopup(false); handleSend("sms"); }} testid="send-via-sms">Send by SMS</MenuItem>
                        <MenuItem icon={EnvelopeSimple} onClick={() => { setSendPopup(false); handleSend("email"); }} testid="send-via-email">Send by email</MenuItem>
                      </div>
                    </>
                  )}
                </div>
                <InvoicePrintMenu
                  invoiceId={invoice.id}
                  saleId={invoice.backing_sale_id || (invoice._is_sale ? invoice.id : (invoice.sale_id || payments.find((p) => p.sale_id)?.sale_id || null))}
                />
                <button onClick={handlePdf} className={`${FOOT_BTN} hidden sm:flex`} data-testid="inv-download-pdf">
                  <Download size={15} weight="bold" /> PDF
                </button>
              </div>

              {/* More menu — destructive actions live here so they aren't tapped by mistake */}
              <div className="relative">
                <button onClick={() => setMoreOpen((v) => !v)} className={FOOT_BTN} data-testid="inv-more-btn">
                  <DotsThree size={18} weight="bold" /> More
                </button>
                {moreOpen && (
                  <>
                    <div className="fixed inset-0 z-[10]" onClick={() => setMoreOpen(false)} />
                    <div className="absolute bottom-full right-0 mb-2 z-[11] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden w-56 py-1" data-testid="inv-more-menu">
                      <MenuItem icon={CopySimple} onClick={() => { setMoreOpen(false); copyLink(); }} testid="inv-copy-link">Copy invoice link</MenuItem>
                      <MenuItem icon={ClockCounterClockwise} onClick={() => { setMoreOpen(false); setHistoryOpen(true); }} testid="inv-history-btn">History</MenuItem>
                      <div className="sm:hidden">
                        <MenuItem icon={Download} onClick={() => { setMoreOpen(false); handlePdf(); }} testid="inv-more-pdf">Download PDF</MenuItem>
                      </div>
                      {!!reviewUrl && (
                        <MenuItem icon={Star} tone="amber" onClick={() => { setMoreOpen(false); setReviewPopup({ open: true, phone: invoice.customer_phone || "" }); }} testid="inv-review-btn">
                          {reviewSending ? "Sending…" : "Ask for Google review"}
                        </MenuItem>
                      )}
                      {!isVoided && <div className="my-1 border-t border-slate-100" />}
                      {hasPayments && !isVoided && (
                        <MenuItem icon={ArrowCounterClockwise} tone="red" onClick={() => { setMoreOpen(false); setRefundOpen(true); }} testid="inv-refund">Refund</MenuItem>
                      )}
                      {!isVoided && (
                        <MenuItem icon={Prohibit} tone="amber" onClick={() => { setMoreOpen(false); requirePermission("sale.void", "Void invoice", handleVoid); }} testid="inv-void">
                          {actionLoading === "void" ? "Voiding…" : "Void invoice"}
                        </MenuItem>
                      )}
                      {!isVoided && (
                        <MenuItem icon={Trash} tone="red" onClick={() => { setMoreOpen(false); requirePermission("sale.delete", "Delete invoice", handleDelete, true); }} testid="inv-delete">
                          {actionLoading === "delete" ? "Deleting…" : "Delete invoice"}
                        </MenuItem>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Built-in Add Payment (used when no external onAddPayment is provided) */}
      {addPayOpen && (
        <AddPaymentModal
          invoice={invoice}
          user={user}
          onClose={() => setAddPayOpen(false)}
          onDone={() => { setAddPayOpen(false); onUpdated(); }}
        />
      )}

      {historyOpen && (
        <div className="fixed inset-0 z-[10000] bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setHistoryOpen(false)}>
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="inv-history-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[15px] font-bold text-slate-900">History · {invoice.invoice_number || invoice.sale_number}</div>
              <button onClick={() => setHistoryOpen(false)} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600" data-testid="inv-history-close">
                <X size={14} weight="bold" />
              </button>
            </div>
            <HistoryPanel
              invoiceId={invoice._is_sale ? null : invoice.id}
              ticketId={invoice.linked_ticket_id || (invoice.items || []).find((i) => i.repair_ticket_id)?.repair_ticket_id}
              saleId={invoice._is_sale ? invoice.id : (invoice.backing_sale_id || invoice.sale_id)}
            />
          </div>
        </div>
      )}

      {/* Refund modal */}
      {refundOpen && (
        <RefundModal
          invoice={invoice}
          user={user}
          onClose={() => setRefundOpen(false)}
          onDone={() => { setRefundOpen(false); onUpdated(); }}
        />
      )}

      {editingPayment && (
        <EditManualPaymentModal
          saleId={invoice._is_sale ? invoice.id : editingPayment.sale_id}
          payment={editingPayment}
          maxAmount={Math.max(0, Number(invoice.total || 0) - payments.filter((x) => x !== editingPayment && !x.corrected_at && !(x.is_refund || x.amount < 0)).reduce((a, x) => a + Number(x.amount || 0), 0))}
          onClose={() => setEditingPayment(null)}
          onDone={() => { setEditingPayment(null); onUpdated(); }}
        />
      )}

      {correctingPayment && (
        <CorrectManualPaymentModal
          saleId={invoice._is_sale ? invoice.id : correctingPayment.sale_id}
          payment={correctingPayment}
          onClose={() => setCorrectingPayment(null)}
          onDone={() => { setCorrectingPayment(null); onUpdated(); }}
        />
      )}

      {/* PIN prompt for void/delete — always required, mirrors the backend's
          require_pin_and_permission gate on the equivalent /sales endpoints. */}
      {pinAction && (
        <PinPromptModal
          open={!!pinAction}
          title={pinAction.label}
          description={`Enter your PIN to ${pinAction.label.toLowerCase()}. This cannot be undone.`}
          confirmLabel={pinAction.label.toUpperCase()}
          destructive
          statusEndpoint={pinAction.adminOnly || user?.role === "admin" ? "/users/me/pin-status" : "/staff-pin/status"}
          onClose={() => setPinAction(null)}
          onConfirm={async (pin) => { await pinAction.fn(pin); setPinAction(null); }}
        />
      )}

      {/* Google Review SMS popup */}
      {reviewPopup.open && (
        <div
          className="fixed inset-0 z-[10000] bg-black/55 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => !reviewSending && setReviewPopup({ open: false, phone: "" })}
          data-testid="review-popup-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-[0_24px_64px_rgba(251,191,36,0.32)]"
          >
            <div className="px-6 pt-6 pb-5 text-white" style={{ background: "linear-gradient(135deg, #F59E0B 0%, #B45309 50%, #78350F 100%)" }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.28)" }}>
                  <Star size={22} weight="fill" />
                </div>
                <div>
                  <div className="font-semibold text-[17px] leading-tight">Send Google Review request</div>
                  <div className="text-[10.5px] mt-1 font-mono uppercase tracking-[0.14em] opacity-80">Verify details before sending</div>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-[10px] tracking-[0.14em] uppercase text-[#9CA3AF] font-semibold">To</div>
                <div className="mt-1 text-[14px] font-semibold text-[#0F172A]">{invoice.customer_name || "Walk-in customer"}</div>
              </div>
              <label className="block">
                <div className="text-[10px] tracking-[0.14em] uppercase text-[#9CA3AF] font-semibold mb-2">SMS to (E.164)</div>
                <input
                  type="tel"
                  value={reviewPopup.phone}
                  onChange={(e) => setReviewPopup((m) => ({ ...m, phone: e.target.value }))}
                  placeholder="+61400123456"
                  data-testid="review-phone-input"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:outline-none focus:border-[#F59E0B] focus:ring-4 focus:ring-[#FBBF24]/25 text-[14px] font-mono bg-white"
                  autoFocus
                />
              </label>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-[10px] tracking-[0.14em] uppercase text-[#92400E] font-semibold">Message preview</div>
                <div className="mt-2 text-[12.5px] text-[#0F172A] leading-relaxed">
                  Hi {(invoice.customer_name || "there").split(" ")[0]}, thanks for choosing {storeName}!
                  Loved your repair? Please leave us a 5-star Google review:
                  <div className="mt-1 font-mono text-[11px] text-[#92400E] break-all underline">{reviewUrl}</div>
                </div>
                <div className="mt-2 text-[10px] text-[#92400E]/70 italic">Final SMS body is rendered server-side; this is a representative preview.</div>
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setReviewPopup({ open: false, phone: "" })}
                  disabled={reviewSending}
                  data-testid="review-cancel"
                  className="flex-1 h-12 rounded-full border border-slate-300 bg-white text-slate-700 text-[13px] font-semibold hover:bg-slate-50 transition-colors active:scale-[0.98]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReview}
                  disabled={reviewSending || !(reviewPopup.phone || "").trim()}
                  data-testid="review-send"
                  className="flex-1 h-12 rounded-full text-white text-[14px] font-semibold disabled:bg-slate-400 transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{ background: reviewSending ? "#9CA3AF" : "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)" }}
                >
                  <Star size={14} weight="fill" />
                  {reviewSending ? "Sending..." : "Send review SMS"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   AddPaymentModal — Quick Cash / Tyro / Card payment recording
   ═══════════════════════════════════════════════════════════════════ */

export function AddPaymentModal({ invoice, user, onClose, onDone }) {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [tyroOpen, setTyroOpen] = useState(false);
  const idemRef = useRef("");
  const inFlight = useRef(false);

  // Same ledger-first rule as the invoice view, so the prefilled amount is
  // what's actually owing even if the stored amount_due lags behind.
  const total = parseFloat(invoice.total || 0);
  const paid = (invoice.payment_history || [])
    .reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : parseFloat(p.amount || 0)), 0);
  const amountDue = (invoice.payment_history || []).length
    ? Math.round(Math.max(0, total - paid) * 100) / 100
    : (invoice.amount_due != null ? parseFloat(invoice.amount_due) : total);

  // Taking money needs no PIN — the `sale.create` permission decides who can
  // do it. PINs stay on the destructive flows (correct / void / refund / delete).
  const recordPayment = async (methodChoice, tyroMeta = null) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const val = parseFloat(amount);
    setSaving(true);
    try {
      const apiMethod = methodChoice === "card" ? "card_manual" : methodChoice;
      // One key per payment attempt — replayed on retry so a dropped
      // response can never record the tender twice.
      if (!idemRef.current) idemRef.current = `inv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const payload = { method: apiMethod, amount: val, idempotency_key: idemRef.current };
      if (tyroMeta) payload.tyro_meta = tyroMeta;
      const saleId = invoice.backing_sale_id || (invoice._is_sale ? invoice.id : null);
      if (saleId) {
        await api.post(`/sales/${saleId}/payments`, payload);
      } else {
        await api.post(`/online-invoices/${invoice.id}/add-payment`, payload);
      }
      toast.success(`$${val.toFixed(2)} ${methodChoice} payment recorded`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
      if (tyroMeta) throw e; // Tyro dialog keeps the approval + offers Retry save
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  const handleSubmit = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return toast.error("Enter a valid amount");
    if (method === "tyro") {
      setTyroOpen(true);
      return;
    }
    recordPayment(method);
  };

  const handleTyroApproved = async (meta) => {
    await recordPayment("tyro", meta);
    setTyroOpen(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-[10000] bg-slate-900/60 flex items-center justify-center p-3" onClick={onClose}>
        <div
          className="bg-white w-full max-w-md rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="add-payment-modal"
        >
          <div className="px-6 pt-6 pb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">TAKE PAYMENT</div>
            <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight mt-0.5">Settle balance on {invoice.invoice_number}</h3>
            <div className="text-[12px] text-slate-500 mt-0.5">
              {invoice.customer_name || "Walk-in"} · total ${money(invoice.total)}
            </div>
            <div className="mt-3 rounded-xl border-2 px-3 py-2 flex items-baseline justify-between"
                 style={{ background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)", borderColor: "#DC2626" }}>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#DC2626]">Outstanding balance</span>
              <span className="font-mono font-bold text-[20px] text-[#DC2626] tabular-nums">${money(amountDue)}</span>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Amount</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-mono">$</span>
                <input
                  type="number" min="0" step="0.01" max={amountDue}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full h-12 pl-7 pr-3 rounded-xl border border-slate-200 focus:border-[#DC2626] focus:ring-2 focus:ring-red-100 focus:outline-none text-lg font-mono font-semibold"
                  data-testid="payment-amount"
                  autoFocus
                />
              </div>
              <div className="flex gap-1.5 mt-2">
                <button type="button" onClick={() => setAmount(amountDue.toFixed(2))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700">EXACT</button>
                {[20, 50, 100].map((v) => (
                  <button key={v} type="button" onClick={() => setAmount(String(Math.min(v, amountDue)))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-mono font-semibold text-slate-700">${v}</button>
                ))}
              </div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Method</div>
              <div className="grid grid-cols-4 gap-2">
                {["cash", "card", "tyro", "other"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMethod(m)}
                    data-testid={`method-${m}`}
                    className={`h-10 rounded-lg text-[11px] tracking-wider font-semibold transition-all flex items-center justify-center gap-1.5 ${
                      method === m
                        ? "bg-[#121216] text-white"
                        : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#121216]"
                    }`}
                  >
                    {m === "tyro" ? (
                      <>
                        <span>TYRO</span>
                        <img src="/tyro-logo.gif" alt="" className="h-3 w-auto" style={method === m ? { filter: "invert(1) brightness(2)" } : undefined} />
                      </>
                    ) : m === "card" ? "CARD" : m.toUpperCase()}
                  </button>
                ))}
              </div>
            </label>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold">CANCEL</button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving || !amount || parseFloat(amount) <= 0}
                data-testid="confirm-payment"
                className="flex-1 h-11 rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[13px] font-semibold shadow-md shadow-red-500/25"
              >
                {saving ? "RECORDING..." : "RECORD PAYMENT"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {tyroOpen && (
        <TyroPaymentDialog
          mode="purchase"
          amountCents={Math.round(parseFloat(amount || 0) * 100)}
          onClose={() => setTyroOpen(false)}
          onApproved={handleTyroApproved}
        />
      )}
    </>
  );
}
